import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import SpeakerAssignmentBeneficiaryChoice from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentChoices/SpeakerAssignmentBeneficiaryChoice';
import SpeakerAssignmentContactChoice from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/SpeakerAssignmentChoices/SpeakerAssignmentContactChoice';
import type InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import type BeneficiaryContactsListView from '@UI/Store/BeneficiaryContacts/Views/BeneficiaryContactsListView';
import type TranscriptSpeakerView from '@UI/Store/Interviews/Views/TranscriptSpeakerView';
import { css } from '@emotion/react';
import * as React from 'react';

const BENEFICIARY_KIND = 'beneficiary';
const CONTACT_KIND = 'contact';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

type ContactCandidate = {
	readonly id: number;
	readonly label: string;
};

type Props = {
	readonly facilityId: number;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly speaker: TranscriptSpeakerView;
	readonly otherSpeakers: readonly TranscriptSpeakerView[];
	readonly participants: readonly InternalInterviewParticipantView[];
	readonly contacts: BeneficiaryContactsListView | null;
};

/**
 * The two entity choices of "Qui parle ?" (§9.5): the accompanied person, and a dossier contact —
 * the interview's own participants first, since they are the most likely match, then the rest of
 * the dossier's contacts. A `staff` participant (a professional) never enters this list: it is not
 * a `BeneficiaryContact`, and a professional is assigned through the free-name field instead (D-2).
 * A dossier without a beneficiary has neither choice: only the free-name field remains.
 */
const SpeakerAssignmentChoices: React.FC<Props> = ({
	facilityId,
	subject,
	interviewId,
	speaker,
	otherSpeakers,
	participants,
	contacts,
}) => {
	const beneficiaryHolder = React.useMemo(
		() => otherSpeakers.find((entry) => entry.kind.value === BENEFICIARY_KIND) ?? null,
		[otherSpeakers],
	);

	const contactCandidates = React.useMemo<ContactCandidate[]>(() => {
		const participantContacts = participants.filter((participant) => participant.kind === 'contact');
		const participantContactIds = new Set(participantContacts.map((participant) => participant.id));
		const remainingContacts = (contacts?.list ?? [])
			.filter((contact) => !participantContactIds.has(contact.id))
			.map((contact) => ({ id: contact.id, label: contact.fullName }));

		return [
			...participantContacts.map((participant) => ({ id: participant.id, label: participant.label })),
			...remainingContacts,
		];
	}, [participants, contacts]);

	const isBeneficiarySelected = speaker.kind.value === BENEFICIARY_KIND;
	const beneficiaryDisabledReason =
		beneficiaryHolder !== null
			? `Un seul locuteur peut être la personne accompagnée — ${beneficiaryHolder.name} la porte déjà.`
			: null;

	return (
		<ConditionalLoader data={subject.isBeneficiary}>
			<div css={sectionStyle}>
				<span css={eyebrowStyle}>Qui est-ce ?</span>
				<SpeakerAssignmentBeneficiaryChoice
					facilityId={facilityId}
					subject={subject}
					interviewId={interviewId}
					speakerId={speaker.id}
					selected={isBeneficiarySelected}
					disabledReason={beneficiaryDisabledReason}
				/>
				{contactCandidates.map((candidate) => (
					<SpeakerAssignmentContactChoice
						key={candidate.id}
						facilityId={facilityId}
						subject={subject}
						interviewId={interviewId}
						speakerId={speaker.id}
						contactId={candidate.id}
						label={candidate.label}
						selected={speaker.kind.value === CONTACT_KIND && speaker.contactId === candidate.id}
					/>
				))}
			</div>
		</ConditionalLoader>
	);
};

export default SpeakerAssignmentChoices;
